import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRef } from "react";

import Field from "../../Components/Field";
import HeaderStatusToggle from "../../Components/HeaderStatusToggle";
import Tooltip from "../../../../../Components/common/Tooltip/Tooltip";
import Spinner from "../../../../../Components/common/Spinner/Spinner";
import Backdrop from "../../../../../Components/UI/Backdrop/Backdrop";
import LdapTest from "./LdapTest";

import {
  RequestGetLdapConfigs,
  RequestSetLdapConfigs,
  RequestToggleLdapStatus,
} from "../../../../../store/actions/settingsActions";
import { addNotification } from "../../../../../store/actions/notifierActions";

import "./styles.scss";

const isValidHost = (trimmedInput) => {
  const ipv4Regex =
    /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

  const hostnameRegex = /^(?!\d{1,3}(\.\d{1,3}){1,3}$)([a-zA-Z0-9-]{1,63})(\.[a-zA-Z0-9-]{1,63})*$/;

  return ipv4Regex.test(trimmedInput) || hostnameRegex.test(trimmedInput);
};

const isValidPort = (trimmedInput) => {
  const port = Number(trimmedInput);
  return Number.isInteger(port) && port >= 1 && port <= 65535;
};

const isValidDN = (dn) => {
  if (!dn || dn.trim() === "") return false;
  // Basic DN validation - should contain at least one = and not start with a comma
  return dn.includes("=") && !dn.startsWith(",") && !dn.endsWith(",");
};

const isValidBindIdentity = (value) =>
  isValidDN(value) || /^[^\s@\\]+@[^\s@\\]+$/.test(value) || /^[^\s\\]+\\[^\s\\]+$/.test(value);

const Ldap = () => {
  const dispatch = useDispatch();

  const [configs, setConfigs] = useState({
    host: "",
    port: "389",
    use_ssl: false,
    base_dn: "",
    user_dn: "",
    login_attr: "uid",
    rdn_attr: "uid",
    object_filter: "(objectClass=person)",
    attributes: ["uid", "cn", "mail"],
    active: false,
    bind_user_dn: "",
    bind_user_password: "",
    use_start_tls: false,
    tls_verify: false,
    admin_group_dn: "",
    superadmin_group_dn: "",
    ca_cert_pem: "",
  });

  const [isLdapEnabled, setIsLdapEnabled] = useState(false);
  const [caFileLabel, setCaFileLabel] = useState("");
  const [caPasteOpen, setCaPasteOpen] = useState(false);
  const [caClearStored, setCaClearStored] = useState(false);

  const configsFromDB = useSelector((state) => state.settings.ldapConfigs) || {};
  const getConfigsLoading = useSelector((state) => state.settings.ldapConfigsLoading);
  const setConfigsLoading = useSelector((state) => state.settings.setLdapConfigsLoading);

  const configsFromDBRef = useRef(configsFromDB);

  // LDAP is active when enabled (regardless of configuration state)
  const isLdapActive = isLdapEnabled;

  // Check if LDAP is configured (has settings but may not be enabled)
  const isLdapConfigured = !!configs.host && !!configs.base_dn;

    const toggleActiveStateHandler = () => {
    if (getConfigsLoading || setConfigsLoading) return;
    
    const newActiveState = !isLdapActive;
    dispatch(RequestToggleLdapStatus({ active: newActiveState }));
  };

  useEffect(() => {
    dispatch(RequestGetLdapConfigs());
  }, [dispatch]);

  useEffect(() => {
    configsFromDBRef.current = configsFromDB;
    if (configsFromDB && Object.keys(configsFromDB).length > 0) {
      const {
        host,
        port,
        use_ssl,
        base_dn,
        user_dn,
        login_attr,
        rdn_attr,
        object_filter,
        attributes,
        active,
        bind_user_dn,
        bind_user_password,
        use_start_tls,
        tls_verify,
        admin_group_dn,
        superadmin_group_dn,
      } = configsFromDB;
      setConfigs({
        host: host || "",
        port: port || "389",
        use_ssl: use_ssl || false,
        base_dn: base_dn || "",
        user_dn: user_dn || "",
        login_attr: login_attr || "uid",
        rdn_attr: rdn_attr || "uid",
        object_filter: object_filter || "(objectClass=person)",
        attributes: attributes || ["uid", "cn", "mail"],
        active: active || false,
        bind_user_dn: bind_user_dn || "",
        bind_user_password: bind_user_password || "",
        use_start_tls: use_start_tls || false,
        tls_verify: tls_verify || false,
        admin_group_dn: admin_group_dn || "",
        superadmin_group_dn: superadmin_group_dn || "",
        ca_cert_pem: "",
      });
      setCaFileLabel("");
      setCaPasteOpen(false);
      setCaClearStored(false);
      setIsLdapEnabled(active || false);
    }
  }, [configsFromDB]);

  const inputChangeHandler = (e) => {
    const name = e.target.name;
    let value = e.target.value;

    if (["use_ssl", "use_start_tls", "tls_verify"].includes(name))
      value = value === "true" ? true : false;

    setConfigs((prevConfigs) => ({ ...prevConfigs, [name]: value }));
  };

  const caInfo = (configsFromDB && configsFromDB.ca_cert_info) || null;
  const pendingCaCount = (configs.ca_cert_pem.match(/BEGIN CERTIFICATE/g) || []).length;

  const resetCaInput = () => {
    setConfigs((prev) => ({ ...prev, ca_cert_pem: "" }));
    setCaFileLabel("");
    setCaPasteOpen(false);
  };

  const removeStoredCa = () => {
    resetCaInput();
    setCaClearStored(true);
  };

  const derToPem = (buffer) => {
    const nl = String.fromCharCode(10);
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    const b64 = window.btoa(binary).replace(/(.{64})/g, "$1" + nl);
    return (
      "-----BEGIN CERTIFICATE-----" + nl + b64 + nl + "-----END CERTIFICATE-----"
    );
  };

  const caFileHandler = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const parts = [];
      for (const file of files) {
        const buffer = await file.arrayBuffer();
        const text = new TextDecoder("utf-8").decode(buffer);
        parts.push(text.includes("BEGIN CERTIFICATE") ? text.trim() : derToPem(buffer));
      }
      setConfigs((prev) => ({ ...prev, ca_cert_pem: parts.join(String.fromCharCode(10)) }));
      setCaFileLabel(files.length > 1 ? `${files.length} files selected` : files[0].name);
      setCaPasteOpen(false);
    } catch (err) {
      dispatch(
        addNotification({
          message: "Could not read the certificate file",
          options: { variant: "error" },
          time: 3000,
        }),
      );
    } finally {
      e.target.value = "";
    }
  };

  const checkSaveButtonDisabled = () => {
    // Always allow saving if there are changes, regardless of toggle state
    if (!configsFromDB || Object.keys(configsFromDB).length === 0) {
      // If no configs in DB, allow saving if we have at least host and base_dn
      return !configs.host.trim() || !configs.base_dn.trim();
    }

    const keys = Object.keys(configs);
    for (const key of keys) {
      if (key === "use_ssl" && configs[key] === configsFromDB[key]) continue;
      else if (key === "use_ssl" && configs[key] !== configsFromDB[key]) return false;
      else if (typeof configs[key] === "string" && configs[key]?.trim() === configsFromDB[key])
        continue;
      else if (Array.isArray(configs[key]) && Array.isArray(configsFromDB[key])) {
        if (JSON.stringify(configs[key]) !== JSON.stringify(configsFromDB[key])) return false;
      } else if (configs[key] !== configsFromDB[key]) return false;
    }
    return true;
  };

  const saveHandler = () => {
    if (getConfigsLoading || setConfigsLoading) return;

    const trimmedHost = configs.host.trim();
    const trimmedPort = configs.port.trim();
    const trimmedBaseDN = configs.base_dn.trim();
    const trimmedUserDN = configs.user_dn.trim();

    if (!isValidHost(trimmedHost)) {
      dispatch(
        addNotification({
          message: "Enter a valid host/domain",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }
    if (!isValidPort(trimmedPort)) {
      dispatch(
        addNotification({
          message: "Enter a valid port",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }
    if (!isValidDN(trimmedBaseDN)) {
      dispatch(
        addNotification({
          message: "Enter a valid base DN",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }
    // User DN can be empty for anonymous bind
    if (trimmedUserDN && !isValidDN(trimmedUserDN)) {
      dispatch(
        addNotification({
          message: "Enter a valid user DN or leave empty for anonymous bind",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }
    
    // Bind User DN validation
    const trimmedBindUserDN = configs.bind_user_dn.trim();
    if (trimmedBindUserDN && !isValidBindIdentity(trimmedBindUserDN)) {
      dispatch(
        addNotification({
          message:
            "Enter the bind user as a DN, user@domain or DOMAIN\\user, or leave empty for anonymous bind",
          options: { variant: "error" },
          time: 4000,
        }),
      );
      return;
    }
    
    const editingExistingBind =
      configsFromDB &&
      !!configsFromDB.bind_user_dn &&
      trimmedBindUserDN === (configsFromDB.bind_user_dn || "").trim();
    if (trimmedBindUserDN && !configs.bind_user_password.trim() && !editingExistingBind) {
      dispatch(
        addNotification({
          message: "Bind user password is required when bind user DN is provided",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }

    for (const [field, label] of [
      ["admin_group_dn", "Admin Group DN"],
      ["superadmin_group_dn", "Superadmin Group DN"],
    ]) {
      const value = configs[field].trim();
      if (value && !isValidDN(value)) {
        dispatch(
          addNotification({
            message: `Enter a valid ${label} (e.g. cn=CF-Admins,ou=Groups,dc=example,dc=com) or leave it empty`,
            options: { variant: "error" },
            time: 4000,
          }),
        );
        return;
      }
    }

    const hasStoredCa = !caClearStored && !!(configsFromDB && configsFromDB.ca_cert);
    const pastedCa = configs.ca_cert_pem.trim();
    if (configs.tls_verify && !hasStoredCa && !pastedCa) {
      dispatch(
        addNotification({
          message:
            "Paste the CA certificate before turning Verify Certificate on, or set it to NO",
          options: { variant: "error" },
          time: 4000,
        }),
      );
      return;
    }
    if (pastedCa && !pastedCa.includes("BEGIN CERTIFICATE")) {
      dispatch(
        addNotification({
          message: "The CA certificate must be PEM text (-----BEGIN CERTIFICATE-----)",
          options: { variant: "error" },
          time: 4000,
        }),
      );
      return;
    }

    dispatch(
      RequestSetLdapConfigs({
        host: trimmedHost,
        port: trimmedPort,
        use_ssl: configs.use_ssl,
        base_dn: trimmedBaseDN,
        user_dn: trimmedUserDN,
        login_attr: configs.login_attr,
        rdn_attr: configs.rdn_attr,
        object_filter: configs.object_filter,
        attributes: configs.attributes,
        active: isLdapEnabled,
        bind_user_dn: trimmedBindUserDN,
        bind_user_password: configs.bind_user_password,
        use_start_tls: configs.use_start_tls,
        tls_verify: configs.tls_verify,
        admin_group_dn: configs.admin_group_dn.trim(),
        superadmin_group_dn: configs.superadmin_group_dn.trim(),
        ca_cert_pem: pastedCa,
        ca_cert_clear: caClearStored,
      }),
    );
  };

  return (
    <div className="ldap">
      <div className="ldap__header">
        <h1>LDAP</h1>
      </div>
      <div className="ldap__body">
        <div className="ldap__body__header">
          <h3>LDAP</h3>
          <HeaderStatusToggle active={isLdapActive} onClick={toggleActiveStateHandler} />
          <p>NOTE: Configure LDAP server settings below.</p>
        </div>
        <div></div>
        <div className="ldap__body__content">
          <div className="ldap__line">
            <div className="p-r-20">
              <Field
                label="Host / Domain"
                tooltipText="• Must be a valid IPv4 address (e.g., 192.168.1.10), or
• A valid hostname or domain (e.g., ldap.local or ldap.company.com)
• Each part (label) must be 1–63 characters, using only letters, numbers, or dashes
• Cannot start or end with a dash (-)
• No spaces or special characters allowed
• Do not include protocol (e.g., ldap://) or port (e.g., :389)"
                width="188px"
                name="host"
                value={configs.host}
                onChange={inputChangeHandler}
                placeholder="192.168.1.100"
              />
            </div>
            <Field
              label="Port"
              tooltipText="• Must be a number between 1 and 65535.
• Default LDAP port is 389, LDAPS port is 636.
• No letters, symbols, or spaces allowed.
• Leading and trailing spaces will be ignored."
              width="92px"
              name="port"
              value={configs.port}
              onChange={inputChangeHandler}
              placeholder="389"
            />
          </div>

          <div className="ldap__line">
            <Field
              label="Base DN"
              tooltipText="• The base Distinguished Name for user searches
• Example: dc=example,dc=com
• Must contain at least one = character
• Cannot start or end with a comma"
              width="318px"
              name="base_dn"
              value={configs.base_dn}
              onChange={inputChangeHandler}
              placeholder="dc=example,dc=com"
            />
          </div>

          <div className="ldap__line">
            <Field
              label="User DN"
              tooltipText="• The organizational unit where users are located
• Example: ou=users
• Leave empty for anonymous bind
• Must contain at least one = character if provided
• Cannot start or end with a comma"
              width="318px"
              name="user_dn"
              value={configs.user_dn}
              onChange={inputChangeHandler}
              placeholder="ou=users"
            />
          </div>

          <div className="ldap__line">
            <div className="p-r-20">
              <Field
                label="Login Attribute"
                tooltipText="• The LDAP attribute(s) users log in with, comma separated
• Active Directory: sAMAccountName,userPrincipalName,mail
• OpenLDAP / 389-DS: uid,mail
• The directory's native attributes are always searched as well,
  so a partially wrong value here will not block logins"
                width="188px"
                name="login_attr"
                value={configs.login_attr}
                onChange={inputChangeHandler}
                placeholder="uid"
              />
            </div>
            <Field
              label="RDN Attribute"
              tooltipText="• The Relative Distinguished Name attribute
• Default: uid
• Usually the same as login attribute
• Used to construct user DN for authentication"
              width="92px"
              name="rdn_attr"
              value={configs.rdn_attr}
              onChange={inputChangeHandler}
              placeholder="uid"
            />
          </div>

          <div className="ldap__line">
            <Field
              label="Object Filter"
              tooltipText="• LDAP filter to identify user objects
• Default: (objectClass=person)
• Used to filter search results
• Must be a valid LDAP filter syntax"
              width="318px"
              name="object_filter"
              value={configs.object_filter}
              onChange={inputChangeHandler}
              placeholder="(objectClass=person)"
            />
          </div>

          <div className="ldap__line">
            <Field
              label="Bind User DN (Recommended)"
              tooltipText="• Read-only service account used to look up the user's DN before
  authenticating them — strongly recommended, and required by
  Active Directory, which refuses anonymous searches
• With it, each login performs exactly one bind as the user, so a
  mistyped password cannot trip the account lockout policy
• Without it, the user DN has to be guessed
• Example: cn=svc-cyberfortress,ou=service,dc=example,dc=com
• Active Directory also accepts: svc-cyberfortress@example.com"
              width="318px"
              name="bind_user_dn"
              value={configs.bind_user_dn}
              onChange={inputChangeHandler}
              placeholder="cn=admin,dc=example,dc=com"
            />
          </div>

          <div className="ldap__line">
            <Field
              label="Bind User Password (Optional)"
              tooltipText="• LDAP bind user password
• Required if Bind User DN is provided
• Leave empty for anonymous bind
• When editing, leave empty to keep the current password"
              width="318px"
              name="bind_user_password"
              type="password"
              value={configs.bind_user_password}
              onChange={inputChangeHandler}
              placeholder="Leave empty to keep current password"
            />
          </div>

          <div className="ldap__line">
            <Field
              label="Admin Group DN (Optional)"
              tooltipText="• Members of this directory group sign in with the admin role
• Leave empty to give every LDAP user the plain user role
• Membership is re-read on every login, so removing someone from
  the group in the directory drops their role here too
• Example: cn=CF-Admins,ou=Groups,dc=example,dc=com
• Active Directory nested groups are followed"
              width="318px"
              name="admin_group_dn"
              value={configs.admin_group_dn}
              onChange={inputChangeHandler}
              placeholder="cn=CF-Admins,ou=Groups,dc=example,dc=com"
            />
          </div>

          <div className="ldap__line">
            <Field
              label="Superadmin Group DN (Optional)"
              tooltipText="• Members of this directory group sign in with the superadmin role
• Takes precedence over the admin group
• Leave empty to grant superadmin only through local accounts
• Example: cn=CF-SuperAdmins,ou=Groups,dc=example,dc=com"
              width="318px"
              name="superadmin_group_dn"
              value={configs.superadmin_group_dn}
              onChange={inputChangeHandler}
              placeholder="cn=CF-SuperAdmins,ou=Groups,dc=example,dc=com"
            />
          </div>

          <div className="ldap__line">
            <div className="ldap__radio">
              <p className="ldap__radio__header">
                Use SSL
                <Tooltip
                  text={`If yes, LDAPS (LDAP over SSL) will be used.
If no, standard LDAP will be used.
Note: LDAPS typically uses port 636.`}
                />
              </p>
              <div className="ldap__radio__labels">
                <label>
                  <input
                    className="ldap__radio__input"
                    type="radio"
                    value={true}
                    name="use_ssl"
                    checked={configs.use_ssl}
                    onChange={inputChangeHandler}
                  />
                  <p>YES</p>
                </label>
                <label>
                  <input
                    className="ldap__radio__input"
                    type="radio"
                    value={false}
                    name="use_ssl"
                    checked={!configs.use_ssl}
                    onChange={inputChangeHandler}
                  />
                  <p>NO</p>
                </label>
              </div>
            </div>

            <div className="ldap__radio">
              <p className="ldap__radio__header">
                Use StartTLS
                <Tooltip
                  text={`Upgrades a plain connection (port 389) to TLS.
Ignored when "Use SSL" is on, since LDAPS (636) is already encrypted.
Enable this if the directory requires an encrypted channel on port 389.`}
                />
              </p>
              <div className="ldap__radio__labels">
                <label>
                  <input
                    className="ldap__radio__input"
                    type="radio"
                    value={true}
                    name="use_start_tls"
                    checked={configs.use_start_tls}
                    onChange={inputChangeHandler}
                  />
                  <p>YES</p>
                </label>
                <label>
                  <input
                    className="ldap__radio__input"
                    type="radio"
                    value={false}
                    name="use_start_tls"
                    checked={!configs.use_start_tls}
                    onChange={inputChangeHandler}
                  />
                  <p>NO</p>
                </label>
              </div>
            </div>

            <div className="ldap__radio">
              <p className="ldap__radio__header">
                Verify Certificate
                <Tooltip
                  text={`Validates the directory's TLS certificate against a trusted CA.
Leave off for on-premise servers using a self-signed certificate.`}
                />
              </p>
              <div className="ldap__radio__labels">
                <label>
                  <input
                    className="ldap__radio__input"
                    type="radio"
                    value={true}
                    name="tls_verify"
                    checked={configs.tls_verify}
                    onChange={inputChangeHandler}
                  />
                  <p>YES</p>
                </label>
                <label>
                  <input
                    className="ldap__radio__input"
                    type="radio"
                    value={false}
                    name="tls_verify"
                    checked={!configs.tls_verify}
                    onChange={inputChangeHandler}
                  />
                  <p>NO</p>
                </label>
              </div>
            </div>
          </div>

          <div className="ldap__body__content__row">
            <div className="ldap__ca">
              <p className="settings__label">
                CA Certificate
                <Tooltip
                  text={`Trust anchor used when Verify Certificate is on.
Upload the issuing CA and the root CA, or paste them as PEM.
Stored on the appliance, so it survives a restart.
Leave untouched to keep the certificate already stored.`}
                />
              </p>

              <div className="ldap__ca__stored">
                <p className="ldap__ca__status">
                  {caClearStored
                    ? "Stored certificate will be removed when you save."
                    : caInfo && caInfo.count > 0
                      ? `Stored: ${caInfo.certificates
                          .map((c) => `${c.subject} (until ${c.not_after})`)
                          .join(", ")}`
                      : "No CA certificate stored."}
                </p>
                {caInfo && caInfo.count > 0 && !caClearStored ? (
                  <button type="button" className="ldap__ca__link" onClick={removeStoredCa}>
                    Remove stored certificate
                  </button>
                ) : null}
                {caClearStored ? (
                  <button
                    type="button"
                    className="ldap__ca__link"
                    onClick={() => setCaClearStored(false)}>
                    Undo
                  </button>
                ) : null}
              </div>

              {caFileLabel ? (
                <div className="ldap__ca__file">
                  <span className="ldap__ca__file__name">{caFileLabel}</span>
                  <span className="ldap__ca__file__meta">
                    {pendingCaCount} certificate{pendingCaCount === 1 ? "" : "s"} ready to save
                  </span>
                  <button type="button" className="ldap__ca__link" onClick={resetCaInput}>
                    Remove
                  </button>
                </div>
              ) : caPasteOpen ? (
                <>
                  <textarea
                    className="ldap__ca__input"
                    name="ca_cert_pem"
                    rows={7}
                    spellCheck={false}
                    placeholder="-----BEGIN CERTIFICATE----- ... -----END CERTIFICATE-----"
                    value={configs.ca_cert_pem}
                    onChange={inputChangeHandler}
                  />
                  <div className="ldap__ca__actions">
                    <span className="ldap__ca__hint">
                      {pendingCaCount > 0
                        ? `${pendingCaCount} certificate${pendingCaCount === 1 ? "" : "s"} detected`
                        : "Paste the PEM text above"}
                    </span>
                    <button type="button" className="ldap__ca__link" onClick={resetCaInput}>
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div className="ldap__ca__actions">
                  <label className="ldap__ca__upload">
                    Upload certificate file
                    <input
                      type="file"
                      accept=".pem,.crt,.cer,.der,.cert"
                      multiple
                      onChange={caFileHandler}
                    />
                  </label>
                  <button
                    type="button"
                    className="ldap__ca__link"
                    onClick={() => setCaPasteOpen(true)}>
                    or paste it manually
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <button
              className="primary-button"
              onClick={saveHandler}
              disabled={checkSaveButtonDisabled()}>
              {getConfigsLoading || setConfigsLoading ? <Spinner width={12} height={12} /> : "Save"}
            </button>
            
            {/* <button
              className="secondary-button"
              onClick={() => {
                setConfigs({
                  host: "ldap.forumsys.com",
                  port: "389",
                  use_ssl: false,
                  base_dn: "dc=example,dc=com",
                  user_dn: "", // Boş bırakın - anonymous bind
                  login_attr: "uid",
                  rdn_attr: "uid",
                  object_filter: "(objectClass=person)",
                  attributes: ["uid", "cn", "mail"],
                  active: false,
                  bind_user_dn: "", // Boş bırakın - anonymous bind
                  bind_user_password: "" // Boş bırakın - anonymous bind
                });
                dispatch(
                  addNotification({
                    message: "Example LDAP settings loaded. Click Save to apply.",
                    options: { variant: "info" },
                    time: 4000,
                  }),
                );
              }}
              style={{
                padding: "8px 16px",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px"
              }}>
              Load Example
            </button> */}
          </div>

          <Backdrop
            spinner={false}
            absolute
            show={!isLdapActive}
            message="LDAP is not active! Turn on the Status toggle above to enable LDAP."
            messageStyle={{ paddingTop: 0 }}
          />
        </div>
      </div>

      {/* Test Section - Only show when LDAP is active */}
      {isLdapActive && (
        <div className="ldap__test-section">
          <LdapTest />
        </div>
      )}
    </div>
  );
};

export default Ldap;
